/**
 * A licence's identity fields (#564): the client category and the country the
 * client is based in, shared by the two forms that write them: New licence
 * (`add-licence`) and Edit licence (`licence-detail`).
 *
 * Shared because it is one RULE now, not one shape: both fields are required on
 * create and on an Administrator's edit. The country is checked against the
 * shared list the backend checks (`COUNTRY_CODES`); the category list lives in
 * the database (#604), so the form only requires that one is picked from the
 * options it loaded; the backend refuses a key it does not hold.
 *
 * Pure (no React, no MUI, no fetch).
 */

import { COUNTRY_CODES, type CountryCode, isCountryCode } from '@shared/constants/countries';

import { countryName } from '@/utils/format';

/** The two fields as a form holds them: `''` until the user picks a value. */
export interface LicenceIdentityDraft {
  /** A key of the category list (#604). */
  client_category: string;
  based_in: CountryCode | '';
}

export const emptyIdentity: LicenceIdentityDraft = { client_category: '', based_in: '' };

export type LicenceIdentityErrors = Partial<Record<keyof LicenceIdentityDraft, string>>;

export const CLIENT_CATEGORY_REQUIRED = 'Choose a client category.';
export const BASED_IN_REQUIRED = 'Choose the country the client is based in.';

/** Field-level messages for the identity fields; empty when both are valid. */
export function validateLicenceIdentity(draft: LicenceIdentityDraft): LicenceIdentityErrors {
  const errors: LicenceIdentityErrors = {};
  if (!draft.client_category) errors.client_category = CLIENT_CATEGORY_REQUIRED;
  if (!isCountryCode(draft.based_in)) errors.based_in = BASED_IN_REQUIRED;
  return errors;
}

/** "France" for `FR`, from `Intl.DisplayNames`; `null` when the licence has none. */
export function countryLabel(code: CountryCode | null): string | null {
  return code ? countryName(code) : null;
}

let sortedCountries: readonly CountryCode[] | null = null;

/**
 * Every ISO code, sorted by its English name; the order a person scans a
 * country list in. Computed once: 249 `Intl` lookups per render of a form would
 * be wasted work for a list that never changes.
 */
export function countryOptions(): readonly CountryCode[] {
  sortedCountries ??= [...COUNTRY_CODES].sort((a, b) =>
    countryName(a).localeCompare(countryName(b), 'en'),
  );
  return sortedCountries;
}
